import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { RiCloseLargeFill } from "react-icons/ri";
import { FiUpload, FiImage } from "react-icons/fi";
import toast from "react-hot-toast";
import GameServices from "../../../services/GameServices";
import Loader from "../../../components/Loader";
import { LIVE_YANTRA_IMAGE_URL } from "../../../utils/utils";

const blankSlot = (position) => ({
  position,
  yantra_id: "",
  use_global: true,
  yantra_name: "",
  yantra_img: "",
  file: null,
  preview: "",
});

/**
 * Board editor for one game: which yantra sits in each slot, and whether it
 * keeps the global name and image or gets its own for this game.
 *
 * A slot always resolves to a yantra_manage row on the server, because that
 * id is what gets written into bets and winners. Picking no yantra but typing
 * a name creates a new global yantra rather than inventing an id.
 */
export default function GameYantraModal({ show, game, options, onClose, onSaved }) {
  const gameApi = new GameServices();

  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const objectUrls = useRef([]);

  const totalGhar = Number(game?.total_ghar) || 0;
  const yantraOptions = useMemo(() => options?.yantras || [], [options]);

  const releasePreviews = () => {
    objectUrls.current.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch (_) {}
    });
    objectUrls.current = [];
  };

  useEffect(() => () => releasePreviews(), []);

  useEffect(() => {
    if (!show || !game?.id) return;

    releasePreviews();
    setLoading(true);
    gameApi
      .getBoard(game.id)
      .then((res) => {
        const saved = res?.isSuccess ? res.data.slots || [] : [];
        const byPosition = new Map(saved.map((s) => [Number(s.position), s]));

        setSlots(
          Array.from({ length: totalGhar }, (_, i) => {
            const position = i + 1;
            const row = byPosition.get(position);
            if (!row) return blankSlot(position);
            return {
              position,
              yantra_id: row.yantra_id ?? "",
              use_global: Boolean(row.use_global),
              yantra_name: row.use_global ? "" : row.custom_name || "",
              yantra_img: row.use_global ? "" : row.custom_img || "",
              file: null,
              preview: "",
            };
          }),
        );
      })
      .catch(() => toast.error("Could not load the yantra board"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, game?.id, totalGhar]);

  const patch = (position, changes) =>
    setSlots((list) =>
      list.map((s) => (s.position === position ? { ...s, ...changes } : s)),
    );

  const pickFile = (position, file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Pick an image file");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Image must be under 3 MB");
      return;
    }
    const preview = URL.createObjectURL(file);
    objectUrls.current.push(preview);
    patch(position, { file, preview });
  };

  /** What this slot will actually show once saved. */
  const resolved = (slot) => {
    const global = yantraOptions.find((y) => Number(y.id) === Number(slot.yantra_id));
    if (slot.use_global) {
      return { name: global?.yantra_name || "", img: global?.yantra_img || "" };
    }
    return {
      name: slot.yantra_name || global?.yantra_name || "",
      img: slot.yantra_img || global?.yantra_img || "",
    };
  };

  const filledCount = slots.filter((s) => s.yantra_id || s.yantra_name.trim()).length;

  const save = async () => {
    if (saving) return;

    const used = new Set();
    for (const slot of slots) {
      const hasYantra = Boolean(slot.yantra_id);
      const hasName = Boolean(slot.yantra_name.trim());

      if (!hasYantra && !hasName) {
        toast.error(`Slot ${slot.position} is empty — pick a yantra or give it a name`);
        return;
      }
      if (!slot.use_global && !hasName) {
        toast.error(`Slot ${slot.position} is custom, so it needs a name`);
        return;
      }
      if (hasYantra) {
        if (used.has(String(slot.yantra_id))) {
          toast.error("The same yantra cannot appear twice on one board");
          return;
        }
        used.add(String(slot.yantra_id));
      }
    }

    const form = new FormData();
    form.append(
      "slots",
      JSON.stringify(
        slots.map((s) => ({
          position: s.position,
          yantra_id: s.yantra_id || null,
          use_global: s.use_global ? 1 : 0,
          yantra_name: s.yantra_name.trim(),
          yantra_img: s.yantra_img || "",
        })),
      ),
    );
    slots.forEach((s) => {
      if (s.file) form.append(`slot_${s.position}_img`, s.file);
    });

    setSaving(true);
    try {
      const res = await gameApi.saveBoard(game.id, form);
      if (res?.isSuccess) {
        toast.success(res.message);
        onSaved();
      } else {
        toast.error(res?.message || "Could not save the board");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not save the board");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={saving ? undefined : onClose}
      centered
      backdrop="static"
      size="xl"
      dialogClassName="gm-board-dialog"
    >
      <div className="modal-header border-0 pb-0">
        <div>
          <h5 className="modal-title">Yantra board — {game?.game_name}</h5>
          <p className="gm-board-sub">
            {filledCount} of {totalGhar} slots filled. Untick <b>Use global</b> to give a slot its
            own name and image for this game only.
          </p>
        </div>
        <button className="btn" onClick={onClose} disabled={saving} aria-label="Close">
          <RiCloseLargeFill size={20} />
        </button>
      </div>
      <hr className="my-1" />

      <Modal.Body>
        {loading ? (
          <Loader message="Loading board..." />
        ) : (
          <div className="gm-board">
            {slots.map((slot) => {
              const view = resolved(slot);
              const src = slot.preview || (view.img ? `${LIVE_YANTRA_IMAGE_URL}/${view.img}` : "");

              return (
                <div
                  key={slot.position}
                  className={`gm-slot${slot.use_global ? "" : " is-custom"}`}
                >
                  <div className="gm-slot-head">
                    <span>SLOT {slot.position}</span>
                    {!slot.use_global && <span className="gm-chip">CUSTOM</span>}
                  </div>

                  <div className="gm-thumb">
                    {src ? (
                      <img src={src} alt={view.name || `Slot ${slot.position}`} />
                    ) : (
                      <FiImage size={22} aria-hidden="true" />
                    )}
                  </div>

                  <select
                    className="gm-slot-input"
                    value={slot.yantra_id}
                    onChange={(e) => patch(slot.position, { yantra_id: e.target.value })}
                    aria-label={`Yantra for slot ${slot.position}`}
                  >
                    <option value="">— new yantra —</option>
                    {yantraOptions.map((y) => (
                      <option key={y.id} value={y.id}>
                        {y.yantra_name}
                      </option>
                    ))}
                  </select>

                  {!slot.use_global || !slot.yantra_id ? (
                    <>
                      <input
                        className="gm-slot-input"
                        type="text"
                        placeholder="Yantra name"
                        value={slot.yantra_name}
                        onChange={(e) => patch(slot.position, { yantra_name: e.target.value })}
                        aria-label={`Name for slot ${slot.position}`}
                      />
                      <label className="gm-upload">
                        <FiUpload size={12} />
                        {slot.file ? slot.file.name.slice(0, 16) : "Upload image"}
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={(e) => pickFile(slot.position, e.target.files?.[0])}
                        />
                      </label>
                    </>
                  ) : null}

                  <label className="gm-check">
                    <input
                      type="checkbox"
                      checked={slot.use_global}
                      disabled={!slot.yantra_id}
                      onChange={(e) => patch(slot.position, { use_global: e.target.checked })}
                    />
                    Use global
                  </label>
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>

      <div className="modal-footer border-0">
        <span className="gm-board-foot">
          {filledCount === totalGhar
            ? "Board complete — this game can be started."
            : `${totalGhar - filledCount} slot(s) still empty.`}
        </span>
        <button className="btn btn-light px-4" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button className="btn gm-btn-primary px-4" onClick={save} disabled={saving || loading}>
          {saving ? "Saving..." : "Save Board"}
        </button>
      </div>
    </Modal>
  );
}
